import { Alert, Anchor, Code, List, Stack, Text } from "@mantine/core";
import type { AnalyticsPlatform } from "../../../api.ts";

/**
 * 各平台接入教程正文（供右侧 Drawer 使用，不内联占列表高度）。
 * 与 docs/23-analytics-bot.md 对齐。
 */

export function platformTutorialTitle(platform: AnalyticsPlatform): string {
  switch (platform) {
    case "facebook":
      return "Facebook / Meta 接入教程";
    case "tiktok":
      return "TikTok 接入教程";
    case "google":
      return "Google Ads 接入教程";
    case "sharechat":
      return "ShareChat / Moj 接入教程";
  }
}

export function PlatformTutorialBody(props: { readonly platform: AnalyticsPlatform }) {
  const { platform } = props;
  if (platform === "facebook") return <FacebookGuide />;
  if (platform === "tiktok") return <TikTokGuide />;
  if (platform === "google") return <GoogleGuide />;
  return <ShareChatGuide />;
}

function FacebookGuide() {
  return (
    <Stack gap="sm">
      <Text size="sm" c="dimmed">
        路径：Meta Conversions API。需要 Pixel / 数据集 ID + 该数据集自己生成的 Access Token。
      </Text>
      <List size="sm" withPadding>
        <List.Item>
          打开{" "}
          <Anchor
            href="https://business.facebook.com/events_manager"
            target="_blank"
            rel="noopener noreferrer"
            size="sm"
          >
            Meta 事件管理工具
          </Anchor>
        </List.Item>
        <List.Item>选要上报的 Pixel / 数据集 -&gt; 设置 Settings</List.Item>
        <List.Item>Conversions API -&gt;「生成访问令牌」（需该资产的操作权限）</List.Item>
        <List.Item>复制 Access Token 与 Pixel ID（概览页数字 ID）</List.Item>
        <List.Item>
          本页「添加凭据」保存；同一 Pixel 已存在请「编辑」换 token。投放 URL 带{" "}
          <Code>{"ad_id={{ad.id}}"}</Code> 与 <Code>pixel=</Code>
        </List.Item>
        <List.Item>保存后点列表「测试」：系统会向 Pixel 发 1 条 CAPI 测试事件</List.Item>
      </List>
      <Alert color="yellow" title="测试通过才算 token 能上报">
        <Text size="xs">
          「测试」走 CAPI 写入（POST /events），与事件管理工具「读取数据集」权限不是一回事。只在
          Business Manager 分享资产、未在该 Pixel
          重新生成令牌时，读取可能失败但写入也可能失败。新分享后必须用该 Pixel 新生成的 token
          覆盖保存。
        </Text>
      </Alert>
      <Alert color="gray" variant="light">
        令牌等同密码；系统加密存储。入群转化走 CAPI，不依赖浏览器 Pixel。
      </Alert>
    </Stack>
  );
}

function TikTokGuide() {
  return (
    <Stack gap="sm">
      <Text size="sm" c="dimmed">
        路径：TikTok Events API 2.0。App ID + Secret + Pixel Code，OAuth 取 token。
      </Text>
      <List size="sm" withPadding>
        <List.Item>
          打开{" "}
          <Anchor
            href="https://business-api.tiktok.com/portal"
            target="_blank"
            rel="noopener noreferrer"
            size="sm"
          >
            TikTok for Business 开发者后台
          </Anchor>
          ，创建 App，拿到 App ID / App Secret
        </List.Item>
        <List.Item>
          redirect_uri 填 <Code>https://mgt.deioio.com/oauth/tiktok/callback</Code>
        </List.Item>
        <List.Item>Events Manager 复制 Pixel Code</List.Item>
        <List.Item>本页添加凭据后点「连接 TikTok」完成 OAuth</List.Item>
        <List.Item>系统按日刷新 token；过期后点「重新授权」</List.Item>
      </List>
      <Alert color="green" variant="light">
        投放 URL：
        <Code>ad_id=__AID__&ttclid=__CLICKID__&pixel={"{PixelCode}"}</Code>
      </Alert>
    </Stack>
  );
}

function GoogleGuide() {
  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed">
        路径：Data Manager API（SA JWT，无 developer-token）。本系统用 Service Account JSON 现签
        access_token 上报转化。
      </Text>

      <Alert color="blue" variant="light" title="本页要填的 3 项">
        <List size="sm" spacing={4}>
          <List.Item>
            <Text span fw={600} size="sm">
              Service Account JSON
            </Text>
            ：Cloud 创建 SA 后下载的密钥（含 <Code>client_email</Code> / <Code>private_key</Code>）
          </List.Item>
          <List.Item>
            <Text span fw={600} size="sm">
              Operating Customer ID
            </Text>
            ：要上报的广告账号 10 位 ID（去横线）
          </List.Item>
          <List.Item>
            <Text span fw={600} size="sm">
              Login Customer ID
            </Text>
            ：有 MCC 填 MCC 10 位；无 MCC 与 Operating 相同
          </List.Item>
        </List>
        <Text size="xs" c="dimmed" mt="xs">
          ctId 不在凭据页填：到「广告列表」按广告配置。
        </Text>
      </Alert>

      <Stack gap={4}>
        <Text fw={600}>1. Google Cloud：创建 SA</Text>
        <List size="sm" spacing={4}>
          <List.Item>
            打开{" "}
            <Anchor
              href="https://console.cloud.google.com/iam-admin/serviceaccounts"
              target="_blank"
              rel="noopener noreferrer"
            >
              Cloud Console - 服务账号
            </Anchor>
          </List.Item>
          <List.Item>创建服务账号 -&gt; 密钥 -&gt; 创建 JSON 密钥并下载</List.Item>
          <List.Item>
            记下 SA 邮箱（如 <Code>mip1@xxx.iam.gserviceaccount.com</Code>）
          </List.Item>
        </List>
      </Stack>

      <Stack gap={4}>
        <Text fw={600}>2. 启用 Data Manager API</Text>
        <List size="sm" spacing={4}>
          <List.Item>
            同 Project 打开{" "}
            <Anchor
              href="https://console.cloud.google.com/apis/library/datamanager.googleapis.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              Data Manager API
            </Anchor>{" "}
            并启用
          </List.Item>
        </List>
      </Stack>

      <Stack gap={4}>
        <Text fw={600}>3. Google Ads：SA 加为「标准」用户</Text>
        <List size="sm" spacing={4}>
          <List.Item>有 MCC 时优先在经理账号「访问权限和安全」添加 SA 为标准用户</List.Item>
          <List.Item>无 MCC：在子账号同样操作；Login = Operating</List.Item>
        </List>
        <Alert color="yellow" variant="light" title="常见卡点">
          <List size="xs" spacing={2}>
            <List.Item>「网域不受支持」：改到 MCC 加 SA</List.Item>
            <List.Item>PERMISSION_DENIED：SA 未加或权限不是标准</List.Item>
            <List.Item>API disabled：步骤 2 未开 Data Manager API</List.Item>
          </List>
        </Alert>
      </Stack>

      <Stack gap={4}>
        <Text fw={600}>4. 创建转化操作，记下 ctId</Text>
        <List size="sm" spacing={4}>
          <List.Item>目标 -&gt; 转化 -&gt; 新建；来源选网站/导入点击后转化</List.Item>
          <List.Item>
            详情 URL 的 <Code>ctId=</Code> 数字即 Conversion Action ID
          </List.Item>
        </List>
      </Stack>

      <Stack gap={4}>
        <Text fw={600}>5. 本系统：保存凭据 + 测鉴权 + 绑 ctId</Text>
        <List size="sm" spacing={4}>
          <List.Item>本页上传 JSON，填 Operating / Login，点「测试鉴权」</List.Item>
          <List.Item>
            「广告列表」配 ctId；投放 URL 带 <Code>p=google</Code> + <Code>{"{gclid}"}</Code> +{" "}
            <Code>{"{creative}"}</Code>（不要用 <Code>{"{creative_id}"}</Code>）
          </List.Item>
          <List.Item>
            「频道/群聊」的 Google badge 生成 Final URL；联调看转化记录 sent + 入库成功
          </List.Item>
        </List>
      </Stack>
    </Stack>
  );
}

/** 长 URL / 宏模板：强制折行，避免 Drawer 横向溢出 */
function WrapCode(props: { readonly children: string }) {
  return (
    <Code
      block
      className="admin-code-wrap"
      style={{
        whiteSpace: "pre-wrap",
        wordBreak: "break-all",
        overflowWrap: "anywhere",
        fontSize: 11,
        maxWidth: "100%",
      }}
    >
      {props.children}
    </Code>
  );
}

function ShareChatGuide() {
  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed">
        路径：ShareChat Ads Manager{" "}
        <Text span fw={600}>
          Event Manager -&gt; Mobile app conversion APIs -&gt; Direct Integration（Non-play）
        </Text>
        。TG 短链进群走 S2S GET postback，
        <Text span fw={600}>
          不要用 Website Pixel，也不要走 Play Store install_referrer
        </Text>
        。
      </Text>

      <Alert color="blue" variant="light" title="App Name = ADVID（先搞清再填）">
        <List size="sm" spacing={4}>
          <List.Item>
            Event Manager 向导第 1 步自己填的{" "}
            <Text span fw={600}>
              App Name
            </Text>
            ，文档里写作 <Code>{"{ADVID}"}</Code>，填入本系统凭据即可。
          </List.Item>
          <List.Item>
            <Text span fw={600}>
              不是
            </Text>{" "}
            账号页的 Advertiser Id（如 <Code>144-xxxx</Code>），
            <Text span fw={600}>
              也不是
            </Text>{" "}
            Website Pixel 的 Pixel Id / Sdk Id。
          </List.Item>
          <List.Item>无 Access Token / 无 Bearer；上报 URL path 上带 App Name 即身份。</List.Item>
          <List.Item>
            同一 App Name 在 Event Manager 可出现多条 draft（列表按内部 id 分行），postback path
            仍只认 name 字符串。
          </List.Item>
          <List.Item>
            <Text span fw={600}>
              本系统强制全库唯一
            </Text>
            （跨 Bot、大小写不敏感）。建名无成本，禁止 A/B 广告户共用同一名字，避免撞 S2S path。建议{" "}
            <Code>Brand_Product</Code> 命名。
          </List.Item>
        </List>
      </Alert>

      <Stack gap={4}>
        <Text fw={600}>1. ShareChat 后台：建 Mobile app 数据源</Text>
        <List size="sm" spacing={4}>
          <List.Item>
            打开{" "}
            <Anchor
              href="https://ads.sharechat.com/v2/event-manager"
              target="_blank"
              rel="noopener noreferrer"
            >
              Event Manager
            </Anchor>
          </List.Item>
          <List.Item>
            <Code>Setup New Data Source</Code> -&gt; 选 <Code>Mobile app conversion APIs</Code>
            （不要选 Website Pixels）
          </List.Item>
          <List.Item>
            App Details：填 <Code>App Name</Code>（稳定唯一，如 <Code>MipTgIndia</Code>）+ 选{" "}
            <Code>Not Listed on Playstore</Code>
          </List.Item>
          <List.Item>
            Integration Partner：点 <Code>Direct Integration</Code>
            （会打开帮助文档；也可继续向导 Test Links / Validation / Verification）
          </List.Item>
          <List.Item>
            官方说明：{" "}
            <Anchor
              href="https://ads.sharechat.com/v2/knowledge-centre/help/event-manager-direct-integration-for-mobile-conversion-apis"
              target="_blank"
              rel="noopener noreferrer"
            >
              Direct Integration for Mobile Conversion APIs
            </Anchor>
          </List.Item>
        </List>
      </Stack>

      <Stack gap={4}>
        <Text fw={600}>2. 本系统：保存 App Name</Text>
        <List size="sm" spacing={4}>
          <List.Item>本页「添加 ShareChat」只填 App Name（表单标签：App Name / ADVID）</List.Item>
          <List.Item>无 token 字段；列表显示「S2S 无 token」属正常</List.Item>
        </List>
      </Stack>

      <Stack gap={4}>
        <Text fw={600}>3. 投放 Landing / CTA URL（ShareChat 广告填这个）</Text>
        <Text size="xs" c="dimmed">
          官方 Non-play 要求落地 URL 末尾带宏。clickId + 广告 ID 是归因必需；
          <Code>campaignname</Code> / <Code>gaid</Code> / <Code>userId</Code> 按官方要求一并带上，
          上报时原样回传；<Code>p=sharechat</Code> / <Code>advid=</Code> 为本系统 pin
          凭据用。优先在「频道/群聊」点 ShareChat badge 复制完整链接。
        </Text>
        <WrapCode>
          {
            "https://tg.deioio.com/s/<chatToken>?ad_id=%%AD_ID%%&clickId=%%DELIVERY_IDENTIFIER%%&campaignname=%%CAMPAIGN_NAME%%&gaid=%%ADVERTISER_IDENTIFIER%%&userId=%%PUBLISHER_IDENTIFIER%%&p=sharechat&advid=<AppName>"
          }
        </WrapCode>
        <Text size="xs" c="dimmed">
          clickId 或广告 ID 的宏未展开（仍含 <Code>%%...%%</Code>）会记 macro_invalid，不上报脏
          clickId；campaignname / gaid / userId 未展开时只是不回传这一项（网页流量 gaid 常为空）。
        </Text>
      </Stack>

      <Stack gap={4}>
        <Text fw={600}>4. 入群后本系统自动 S2S 上报</Text>
        <WrapCode>
          {
            "GET https://sc-s2s-external.sharechat.com/a1s-s2s-service/v1/events/<AppName>/post?clickId=...&adId=...&eventName=registration&EventTime=<unix_sec>&eventValue=1"
          }
        </WrapCode>
        <List size="sm" spacing={4}>
          <List.Item>
            默认 eventName=<Code>registration</Code>（「广告」tab 可改成 page_visits / registration
            / app_download / install / transaction 之一，其他名字 ShareChat 不认，系统不上报）
          </List.Item>
          <List.Item>HTTP 2xx 即 sent（无 Google 那种 requestStatus 二阶段）</List.Item>
        </List>
      </Stack>

      <Stack gap={4}>
        <Text fw={600}>5. 上线前验收</Text>
        <List size="sm" spacing={4}>
          <List.Item>
            用 dummy 值打一条 registration postback，path 用同一 App Name + UNIX EventTime
          </List.Item>
          <List.Item>
            邮件{" "}
            <Anchor href="mailto:adssupport@sharechat.co" size="sm">
              adssupport@sharechat.co
            </Anchor>{" "}
            / ad-ops，确认对方收到事件后再放量
          </List.Item>
        </List>
      </Stack>

      <Alert color="yellow" variant="light" title="常见误区">
        <List size="xs" spacing={2}>
          <List.Item>Website Pixel 埋网页点，点不到 Telegram 入群，主转化只能 S2S</List.Item>
          <List.Item>Play Store listed 走 referrer 编码宏，本产品不走这条</List.Item>
          <List.Item>
            宏是 %%NAME%%，不是 Meta 的 {"{{}}"} 或 Google 的 {"{}"}
          </List.Item>
          <List.Item>
            Event Manager 同名多条 draft 不等于多个 ADVID；本系统只录一次 App Name
          </List.Item>
        </List>
      </Alert>
    </Stack>
  );
}
